import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'manufacturer' | 'practitioner' | 'insurance' | 'insurance_verifier';
  permissions: string[];
  isTempPasswordActive?: boolean;
  tempPasswordExpiry?: string;
  needsPasswordChange?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  user: null,
  token: null,
  isLoading: false,
  error: null,
  isAuthenticated: false,
};

// Helper function to map backend user data to frontend user format
const mapBackendUserToFrontend = (backendUser: any): User => {
  return {
    id: backendUser.id || backendUser._id,
    email: backendUser.email,
    name: backendUser.firstName || backendUser.name || '',
    role: backendUser.role,
    permissions: backendUser.permissions || [],
    isTempPasswordActive: backendUser.isTempPasswordActive,
    tempPasswordExpiry: backendUser.tempPasswordExpiry,
    needsPasswordChange: backendUser.needsPasswordChange,
  };
};

// Async thunk for login
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // CRITICAL: Send/receive HTTP-only auth cookie
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.error || 'Login failed');
      }

      if (data.success && data.data) {
        // Store user in localStorage for quick access
        localStorage.setItem('medtriage_user', JSON.stringify(mapBackendUserToFrontend(data.data.user)));
        
        return {
          user: mapBackendUserToFrontend(data.data.user),
          token: null, // Token is in HTTP-only cookie, not in response body
        };
      }

      return rejectWithValue('Invalid response format');
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

// Async thunk for getting current user
export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/auth/me', {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include' // Send HTTP-only auth cookie
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403 || response.status === 404) {
          throw new Error('Token invalid');
        }
        return rejectWithValue('Failed to fetch user data');
      }

      const data = await response.json();

      if (data.success && data.data?.user) {
        return mapBackendUserToFrontend(data.data.user);
      }

      return rejectWithValue('Invalid response format');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Network error');
    }
  }
);

// Async thunk for logout
export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async () => {
    try {
      // Call logout endpoint to clear HTTP-only cookie
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include' // Send HTTP-only cookie so backend can clear it
      });
      
      // Return success so the promise resolves
      return { success: true };
    } catch (error) {
      // Ignore logout API errors, we still want to clear local state
      console.warn('Logout API call failed:', error);
      // Still return success to clear local state
      return { success: true };
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    // Initialize auth state - load from localStorage if available
    initializeAuth: (state) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        state.token = token;
        state.isAuthenticated = true;
        // Note: user data will be fetched separately via getCurrentUser
      }
      state.isLoading = false;
    },
    // Clear auth state
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      state.isLoading = false;
      
      // Clear all localStorage
      localStorage.clear();
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = null; // Token is in HTTP-only cookie, not stored in state
        state.isAuthenticated = true;
        state.error = null;
        
        // No token to store in localStorage - it's in HTTP-only cookie
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      })
      // Get current user cases
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
        
        // Auth state is managed by Redux - no localStorage needed
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        if (action.payload === 'Token invalid') {
          // Clear auth state on token invalidation
          state.user = null;
          state.token = null;
          state.isAuthenticated = false;
        }
        state.error = action.payload as string;
      })
      // Logout cases
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
        state.isLoading = false;
        
        // Clear all localStorage on logout
        localStorage.clear();
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even if logout fails on backend, clear local state
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
        state.isLoading = false;
        
        // Clear all localStorage on logout
        localStorage.clear();
      });
  },
});

export const { clearError, setLoading, initializeAuth, clearAuth } = authSlice.actions;
export default authSlice.reducer;
